import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { isAuthenticated, getStoredUser } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import UserHome from "@/pages/UserHome";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import AdConfig from "@/pages/AdConfig";
import VideoRules from "@/pages/VideoRules";
import Favorites from "@/pages/Favorites";
import Categories from "@/pages/Categories";
import PlacesTester from "@/pages/PlacesTester";
import Secrets from "@/pages/Secrets";
import Settings from "@/pages/Settings";
import AdConfigHistory from "@/pages/AdConfigHistory";
import Analytics from "@/pages/Analytics";
import MobileConnection from "@/pages/MobileConnection";
import Users from "@/pages/Users";
import Referrals from "@/pages/Referrals";
import PagesAdmin from "@/pages/Pages";
import PublicPage from "@/pages/PublicPage";
import "@/App.css";

function RequireUser({ children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  if (!isAuthenticated()) return <Navigate to="/admin/login" replace />;
  const u = getStoredUser();
  // Legacy X-Admin-Token: no user object → treat as owner
  const isAdmin = !u || u.role === "owner" || u.role === "editor" || u.role === "viewer";
  if (!isAdmin) return <Navigate to="/app" replace />;
  return children;
}

function InnerRouter() {
  const location = useLocation();
  // Google callback comes back with #session_id in fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />

      {/* User auth */}
      <Route path="/login" element={<Login mode="user" />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public CMS pages
           Root-level: /support, /privacy-policy, /about
           Legacy /p/:slug kept as a permanent redirect so old links keep working. */}
      <Route path="/p/:slug" element={<LegacyPageRedirect />} />
      {/* NOTE: this must be LAST so specific static routes above (/login, /register,
           /app, /admin, /auth/callback) win. React Router v6 gives priority to
           the more specific match, but keep this near the bottom for clarity. */}

      {/* User area */}
      <Route
        path="/app"
        element={
          <RequireUser>
            <UserHome />
          </RequireUser>
        }
      />

      {/* Admin auth */}
      <Route path="/admin/login" element={<Login mode="admin" />} />

      {/* Admin console */}
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Layout />
          </RequireAdmin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="ads" element={<AdConfig />} />
        <Route path="ads/history" element={<AdConfigHistory />} />
        <Route path="ads/video-rules" element={<VideoRules />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="favorites" element={<Favorites />} />
        <Route path="categories" element={<Categories />} />
        <Route path="places-tester" element={<PlacesTester />} />
        <Route path="mobile-connection" element={<MobileConnection />} />
        <Route path="users" element={<Users />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="pages" element={<PagesAdmin />} />
        <Route path="secrets" element={<Secrets />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="/:slug" element={<PublicPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Backwards-compat helper: /p/:slug → /:slug (permanent redirect). Preserves
 * any bookmarks or shared links from before the URL scheme moved to root.
 */
function LegacyPageRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

function App() {
  useTheme();
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <InnerRouter />
    </BrowserRouter>
  );
}

export default App;
